// Linke Sidebar — EVE-Modi + Mit Doc + Gedächtnis + Letzte Gespräche.
// Source-of-truth: mockups/03-shell/B-bolt-hero.html (linke Spalte).
//
// Modi-Routes laufen über react-router NavLink (active = amber border-left).
// „Neues Gespräch" + History sind in Phase 4 wired (brauchen Chat-Store).

import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import {
  Activity,
  BrainCircuit,
  Brain,
  Calendar,
  Flame,
  History,
  LayoutDashboard,
  LineChart,
  Lock,
  Plus,
  Settings,
  Shield,
  ShieldCheck,
  Vote,
  Users,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useAuth } from '@/hooks/use-auth';
import { useCredits } from '@/hooks/use-credits';
import { useEveMemory } from '@/hooks/use-eve-memory';
import { useEveChatStore } from '@/stores/eve-chat-store';
import type { FeatureKey } from '@/types/tier-config';

interface NavEntry {
  to: string;
  label: string;
  icon: LucideIcon;
  badge?: string;
  /** Feature-Bereich (Credits-Modell: alle offen, nur privat/admin owner-only). */
  feature?: FeatureKey;
  /** Sub-Items werden eingerückt darunter gerendert wenn parent active. */
  children?: ReadonlyArray<NavEntry>;
}

const MODE_ENTRIES: ReadonlyArray<NavEntry> = [
  {
    to: '/markt',
    label: 'Markt',
    icon: LayoutDashboard,
    feature: 'markt',
    children: [
      { to: '/markt/polymarket', label: 'Polymarket', icon: Vote, feature: 'markt' },
      { to: '/markt/trending', label: 'Trending', icon: Flame, feature: 'markt' },
      { to: '/markt/japan-carry', label: 'Japan Carry', icon: Activity, feature: 'markt' },
    ],
  },
  { to: '/trading/macro', label: 'Wirtschaftsnews', icon: LineChart, feature: 'trading' },
  { to: '/hyperliquid', label: 'Hyperliquid', icon: Activity, feature: 'hyperliquid' },
  { to: '/academy', label: 'Academy', icon: Brain, feature: 'academy' },
];

const DOC_ENTRIES: ReadonlyArray<NavEntry> = [
  { to: '/coaching', label: 'Coaching', icon: Calendar, feature: 'coaching' },
  { to: '/community', label: 'Community', icon: Users, feature: 'community' },
];

export function Sidebar() {
  const { profile, isOwner, signOut } = useAuth();
  const { memories, rasRules } = useEveMemory();
  const { balance } = useCredits();
  const navigate = useNavigate();
  const location = useLocation();

  const handleSignOut = () => {
    signOut();
    navigate('/login');
  };

  // Credits-Modell: alle Feature-Bereiche sind offen. privat + admin laufen
  // nur über die owner-only SystemSection unten — also nie hier gelockt.
  const isLocked = (_entry: NavEntry): boolean => false;

  return (
    <aside className="glass-bar flex w-56 shrink-0 flex-col border-r border-white/5">
      {/* "Neues Gespräch" CTA */}
      <button
        type="button"
        className="mx-3 mt-3 flex items-center justify-center gap-2 rounded-lg bg-amber-500 px-3 py-2 text-sm font-medium text-neutral-950 shadow-[0_0_20px_rgba(251,191,36,0.3)] transition hover:bg-amber-400"
      >
        <Plus className="size-4" />
        Neues Gespräch
      </button>

      <nav className="scroll-hidden mt-3 flex-1 space-y-3 overflow-y-auto px-2 pb-2 text-[13px]">
        <Section title="EVE-Modi">
          {MODE_ENTRIES.map((e) => {
            const locked = isLocked(e);
            // Sub-Items werden eingerückt darunter gerendert wenn parent-Pfad
            // im aktuellen Location enthalten ist (also auch wenn man auf
            // /markt/crowd ist — bleibt expandiert).
            const isParentActive = e.children && location.pathname.startsWith(e.to);
            return (
              <div key={`${e.to}-${e.label}`}>
                <NavRow
                  to={e.to}
                  icon={e.icon}
                  label={e.label}
                  badge={locked ? undefined : e.badge}
                  disabled={locked}
                />
                {isParentActive && !locked
                  ? e.children!.map((child) => {
                      const childLocked = isLocked(child);
                      return (
                        <SubNavRow
                          key={`${child.to}-${child.label}`}
                          to={child.to}
                          icon={child.icon}
                          label={child.label}
                          disabled={childLocked}
                        />
                      );
                    })
                  : null}
              </div>
            );
          })}
        </Section>

        <Section title="Mit Doc">
          {DOC_ENTRIES.map((e) => {
            const locked = isLocked(e);
            return (
              <NavRow
                key={`${e.to}-${e.label}`}
                to={e.to}
                icon={e.icon}
                label={e.label}
                disabled={locked}
              />
            );
          })}
        </Section>

        <Section title="Gedächtnis">
          <NavRow
            to="/memory"
            icon={BrainCircuit}
            label="Memory"
            badge={memories.length.toString()}
          />
          <NavRow to="/ras" icon={Shield} label="RAS" badge={rasRules.length.toString()} />
          <NavRow to="/calls" icon={History} label="Calls" />
        </Section>

        <RecentChatsSection currentPath={location.pathname} />

        {isOwner ? (
          <SystemSection>
            <NavRow to="/admin" icon={ShieldCheck} label="Admin" badge="Owner" />
            <NavRow to="/privat" icon={Lock} label="Privat" badge="Owner" />
          </SystemSection>
        ) : null}
      </nav>

      {/* User-Footer */}
      <div className="flex items-center gap-2 border-t border-white/5 p-2">
        <button
          type="button"
          onClick={() => navigate('/settings')}
          className="grid size-6 place-items-center rounded-full bg-gradient-to-br from-amber-400 to-amber-600 text-[12px] font-bold text-neutral-950"
          title={profile?.nickname ?? 'Profil'}
        >
          {(profile?.nickname?.[0] ?? 'D').toUpperCase()}
        </button>
        <div className="min-w-0 flex-1">
          <div className="truncate text-sm font-medium leading-tight">
            {profile?.nickname ?? 'User'}
          </div>
          <button
            type="button"
            onClick={() => navigate('/credits')}
            className="flex items-center gap-1 text-[12px] leading-tight text-amber-300 transition hover:text-amber-200"
            title="Credits aufladen"
          >
            <span aria-hidden>◆</span>
            <span className="tabular">{balance === null ? '—' : balance}</span>
            <span>Credits</span>
          </button>
        </div>
        <button
          type="button"
          onClick={() => navigate('/settings')}
          className="text-neutral-500 hover:text-neutral-200"
          title="Settings"
        >
          <Settings className="size-4" />
        </button>
        <button
          type="button"
          onClick={handleSignOut}
          className="text-neutral-500 hover:text-rose-300"
          title="Ausloggen"
        >
          <span aria-hidden className="text-[14px]">
            ↩
          </span>
        </button>
      </div>
    </aside>
  );
}

interface SectionProps {
  title: string;
  children: React.ReactNode;
}

function Section({ title, children }: SectionProps) {
  return (
    <div>
      <div className="mb-1 px-2 text-[11px] uppercase tracking-wider text-neutral-500">{title}</div>
      <div className="space-y-0.5">{children}</div>
    </div>
  );
}

// ── Letzte Gespräche ────────────────────────────────────────────────────────
// Listet die zuletzt aktiven Pages aus dem Multi-Page-Chat-Store. Jede Page
// hat eigenen byPage[key]. Wir sortieren nach „letzte Message-Timestamp" und
// zeigen die Top-5.

function RecentChatsSection({ currentPath }: { currentPath: string }) {
  const navigate = useNavigate();
  const byPage = useEveChatStore((s) => s.byPage);

  const recent = Object.entries(byPage)
    .map(([key, msgs]) => {
      const last = msgs[msgs.length - 1];
      const ts = last ? Date.parse(last.createdAt) : 0;
      return { key, count: msgs.length, ts: Number.isFinite(ts) ? ts : 0, lastText: last?.text ?? '' };
    })
    .filter((c) => c.count > 0)
    .sort((a, b) => b.ts - a.ts)
    .slice(0, 5);

  if (recent.length === 0) {
    return (
      <Section title="Letzte Gespräche">
        <p className="px-2 text-[11px] italic text-neutral-500">
          Noch keine — chat mit EVE auf einer beliebigen Page.
        </p>
      </Section>
    );
  }

  return (
    <Section title="Letzte Gespräche">
      <ul className="space-y-0.5">
        {recent.map((r) => {
          const active = currentPath === r.key;
          const title = formatPageKey(r.key);
          return (
            <li key={r.key}>
              <button
                type="button"
                onClick={() => navigate(r.key)}
                className={
                  active
                    ? 'flex w-full items-center gap-2 rounded-md border-l-2 border-amber-500 bg-amber-500/10 px-2 py-1 text-left text-[12px] text-amber-300'
                    : 'flex w-full items-center gap-2 rounded-md border-l-2 border-transparent px-2 py-1 text-left text-[12px] text-neutral-300 hover:bg-white/5'
                }
                title={r.lastText.slice(0, 120)}
              >
                <span className="truncate flex-1">{title}</span>
                <span className="shrink-0 text-[10px] text-neutral-500">{r.count}</span>
              </button>
            </li>
          );
        })}
      </ul>
    </Section>
  );
}

function formatPageKey(key: string): string {
  const parts = key.split('/').filter(Boolean);
  if (parts.length === 0) return 'Home';
  return parts[parts.length - 1]!
    .split('-')
    .map((s) => s.charAt(0).toUpperCase() + s.slice(1))
    .join(' ');
}

interface SystemSectionProps {
  children: React.ReactNode;
}

function SystemSection({ children }: SystemSectionProps) {
  return (
    <div className="border-t border-rose-500/20 pt-3">
      <div className="mb-1 flex items-center gap-1.5 px-2 text-[11px] uppercase tracking-wider text-rose-400">
        <Shield className="size-3" />
        <span>System</span>
      </div>
      <div className="space-y-0.5">{children}</div>
    </div>
  );
}

interface NavRowProps {
  to: string;
  icon: LucideIcon;
  label: string;
  badge?: string;
  disabled?: boolean;
}

function NavRow({ to, icon: Icon, label, badge, disabled }: NavRowProps) {
  if (disabled) {
    return (
      <div className="flex cursor-not-allowed items-center gap-2 rounded-lg px-2 py-1.5 text-neutral-600">
        <Icon className="size-3.5" />
        <span className="flex-1">{label}</span>
        {badge ? (
          <span className="rounded bg-neutral-800 px-1 text-[11px] text-neutral-500">{badge}</span>
        ) : null}
      </div>
    );
  }
  return (
    <NavLink
      to={to}
      className={({ isActive }) =>
        [
          'flex items-center gap-2 rounded-lg px-2 py-1.5 transition',
          isActive
            ? 'border-l-2 border-amber-500 bg-amber-500/15 text-amber-300'
            : 'text-neutral-400 hover:bg-white/5 hover:text-white',
        ].join(' ')
      }
      end={true}
    >
      <Icon className="size-3.5" />
      <span className="flex-1">{label}</span>
      {badge ? (
        <span className="text-[11px] text-neutral-500">{badge}</span>
      ) : null}
    </NavLink>
  );
}

/** Eingerückte Sub-Row unter einem Parent-NavItem (z.B. Markt → Crowd). */
function SubNavRow({ to, icon: Icon, label, disabled }: NavRowProps) {
  if (disabled) {
    return (
      <div className="ml-5 flex cursor-not-allowed items-center gap-2 rounded-lg px-2 py-1 text-[12px] text-neutral-600">
        <Icon className="size-3" />
        <span className="flex-1">{label}</span>
      </div>
    );
  }
  return (
    <NavLink
      to={to}
      end={true}
      className={({ isActive }) =>
        [
          'ml-5 flex items-center gap-2 rounded-lg px-2 py-1 text-[12px] transition',
          isActive
            ? 'border-l-2 border-amber-500/60 bg-amber-500/10 text-amber-300'
            : 'border-l-2 border-white/5 text-neutral-500 hover:bg-white/5 hover:text-white',
        ].join(' ')
      }
    >
      <Icon className="size-3" />
      <span className="flex-1">{label}</span>
    </NavLink>
  );
}
